import React, { useState } from 'react';
import logger from '../../utils/logger';
import { EmailSettings } from '../../types';
import { Button } from '../ui';
import { EmailInput } from '../forms/EmailInput';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PlusIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface EmailSettingsFormProps {
  settings: EmailSettings;
  onUpdate: (settings: Partial<EmailSettings>) => Promise<void>;
  isLoading: boolean;
}

const EmailSettingsForm: React.FC<EmailSettingsFormProps> = ({ settings, onUpdate, isLoading }) => {
  const [newNotificationEmail, setNewNotificationEmail] = useState('');
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewEmailValid, setIsNewEmailValid] = useState(false);
  const [_isPrimaryEmailValid, setIsPrimaryEmailValid] = useState(true);

  const getVerificationStatusColor = (status: EmailSettings['emailVerificationStatus']) => {
    switch (status) {
      case 'verified':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getVerificationStatusText = (status: EmailSettings['emailVerificationStatus']) => {
    switch (status) {
      case 'verified':
        return '認証済み';
      case 'pending':
        return '認証待ち';
      case 'failed':
        return '認証失敗';
      default:
        return '不明';
    }
  };

  const handlePrimaryEmailChange = async (email: string) => {
    await onUpdate({
      primaryEmail: email,
      emailVerificationStatus: 'pending',
      lastVerificationSent: new Date().toISOString(),
    });
  };

  const handleNotificationToggle = async (
    key: keyof EmailSettings['emailNotifications'],
    value: boolean
  ) => {
    await onUpdate({
      emailNotifications: {
        ...settings.emailNotifications,
        [key]: value,
      },
    });
  };

  const handleAddNotificationEmail = async () => {
    if (!newNotificationEmail.trim()) return;

    if (!isNewEmailValid) {
      setError('有効なメールアドレスを入力してください');
      return;
    }

    if (settings.notificationEmails.includes(newNotificationEmail)) {
      setError('このメールアドレスは既に登録されています');
      return;
    }

    setError(null);

    await onUpdate({
      notificationEmails: [...settings.notificationEmails, newNotificationEmail],
    });

    setNewNotificationEmail('');
    setIsAddingEmail(false);
    setError(null); // 成功時にエラーをクリア
    setIsNewEmailValid(false);
  };

  const handleRemoveNotificationEmail = async (emailToRemove: string) => {
    await onUpdate({
      notificationEmails: settings.notificationEmails.filter(email => email !== emailToRemove),
    });
  };

  const handleResendVerification = async () => {
    // TODO: メール認証再送信の実装
    await onUpdate({
      emailVerificationStatus: 'pending',
      lastVerificationSent: new Date().toISOString(),
    });
    logger.info('認証メール再送信（未実装）');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  // メールアドレスバリデーション結果ハンドラー
  const handlePrimaryEmailValidationChange = (valid: boolean) => {
    setIsPrimaryEmailValid(valid);
  };

  const handleNewEmailValidationChange = (valid: boolean) => {
    setIsNewEmailValid(valid);
  };

  // メールアドレス変更ハンドラー
  const handlePrimaryEmailChangeWrapper = (_name: string, value: string) => {
    handlePrimaryEmailChange(value);
  };

  const handleNewEmailChange = (_name: string, value: string) => {
    setNewNotificationEmail(value);
    setError(null);
  };

  return (
    <div className='space-y-8'>
      {/* プライマリメールアドレス */}
      <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-6'>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center space-x-2'>
          <EnvelopeIcon className='h-5 w-5' />
          <span>プライマリメールアドレス</span>
        </h3>

        <div className='space-y-4'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <div>
              <EmailInput
                name='primary-email'
                label='メールアドレス'
                value={settings.primaryEmail}
                onChange={handlePrimaryEmailChangeWrapper}
                onValidationChange={handlePrimaryEmailValidationChange}
                placeholder='admin@example.com'
                required={true}
                disabled={isLoading}
                validationOptions={{ required: true }}
                data-testid='primary-email-input'
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                認証ステータス
              </label>
              <div className='flex items-center space-x-3'>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getVerificationStatusColor(
                    settings.emailVerificationStatus
                  )}`}
                >
                  {settings.emailVerificationStatus === 'verified' ? (
                    <CheckCircleIcon className='h-4 w-4 mr-2' />
                  ) : (
                    <ExclamationTriangleIcon className='h-4 w-4 mr-2' />
                  )}
                  {getVerificationStatusText(settings.emailVerificationStatus)}
                </span>

                {settings.emailVerificationStatus !== 'verified' && (
                  <Button
                    variant='secondary'
                    size='sm'
                    onClick={handleResendVerification}
                    disabled={isLoading}
                  >
                    認証メール再送信
                  </Button>
                )}
              </div>

              {settings.lastVerificationSent && settings.emailVerificationStatus === 'pending' && (
                <p className='mt-2 text-sm text-gray-500 flex items-center space-x-1'>
                  <ClockIcon className='h-4 w-4' />
                  <span>最後の送信: {formatDate(settings.lastVerificationSent)}</span>
                </p>
              )}
            </div>
          </div>

          <p className='text-sm text-gray-500'>
            システムからの重要な通知やアカウント関連の連絡が送信されます
          </p>
        </div>
      </div>

      {/* 通知設定 */}
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>メール通知設定</h3>

        <div className='space-y-4'>
          {[
            {
              key: 'scheduleChanges' as const,
              label: 'スケジュール変更通知',
              description: '配車スケジュールの変更や更新時に通知を受け取る',
            },
            {
              key: 'systemAlerts' as const,
              label: 'システムアラート',
              description: 'システムエラーやメンテナンス情報の通知を受け取る',
            },
            {
              key: 'billingUpdates' as const,
              label: '請求・支払い通知',
              description: '請求書発行や支払い完了時の通知を受け取る',
            },
            {
              key: 'securityNotifications' as const,
              label: 'セキュリティ通知',
              description: 'ログイン試行やセキュリティ関連の通知を受け取る',
            },
            {
              key: 'weeklyReports' as const,
              label: '週次レポート',
              description: '業務実績の週次レポートを受け取る',
            },
          ].map(({ key, label, description }) => (
            <div
              key={key}
              className='flex items-start space-x-3 p-4 border border-gray-200 dark:border-gray-600 rounded-lg'
            >
              <input
                id={key}
                type='checkbox'
                checked={settings.emailNotifications[key]}
                onChange={e => handleNotificationToggle(key, e.target.checked)}
                disabled={isLoading}
                className='mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded'
              />
              <div className='flex-1'>
                <label
                  htmlFor={key}
                  className='text-sm font-medium text-gray-900 dark:text-white cursor-pointer'
                >
                  {label}
                </label>
                <p className='text-sm text-gray-500 mt-1'>{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 追加通知先メールアドレス */}
      <div>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
            追加通知先メールアドレス
          </h3>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => setIsAddingEmail(true)}
            disabled={isLoading || isAddingEmail}
            className='flex items-center space-x-2'
          >
            <PlusIcon className='h-4 w-4' />
            <span>追加</span>
          </Button>
        </div>

        <p className='text-sm text-gray-500 mb-4'>
          メイン管理者以外にも通知を送信したい場合に追加してください
        </p>

        {/* 新しいメールアドレス追加フォーム */}
        {isAddingEmail && (
          <div className='mb-4 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700'>
            {/* エラー表示 */}
            {error && (
              <div className='mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg'>
                <div className='flex items-start space-x-2'>
                  <ExclamationTriangleIcon className='h-5 w-5 text-red-500 mt-0.5' />
                  <p className='text-sm text-red-700 dark:text-red-400'>{error}</p>
                </div>
              </div>
            )}
            <div className='flex items-center space-x-3'>
              <EmailInput
                name='new-notification-email'
                value={newNotificationEmail}
                onChange={handleNewEmailChange}
                onValidationChange={handleNewEmailValidationChange}
                placeholder='notification@example.com'
                required={false}
                disabled={isLoading}
                className='flex-1'
                data-testid='new-notification-email-input'
              />
              <Button
                variant='primary'
                size='sm'
                onClick={handleAddNotificationEmail}
                disabled={isLoading || !newNotificationEmail.trim() || !isNewEmailValid}
              >
                追加
              </Button>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => {
                  setIsAddingEmail(false);
                  setNewNotificationEmail('');
                  setError(null); // キャンセル時にエラーをクリア
                }}
                disabled={isLoading}
              >
                キャンセル
              </Button>
            </div>
          </div>
        )}

        {/* 登録済みメールアドレス一覧 */}
        {settings.notificationEmails.length === 0 ? (
          <div className='text-center py-8 text-gray-500'>
            <EnvelopeIcon className='h-12 w-12 mx-auto mb-4 text-gray-300' />
            <p>追加の通知先メールアドレスは登録されていません</p>
          </div>
        ) : (
          <div className='space-y-2'>
            {settings.notificationEmails.map((email, index) => (
              <div
                key={index}
                className='flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg'
              >
                <div className='flex items-center space-x-3'>
                  <EnvelopeIcon className='h-5 w-5 text-gray-400' />
                  <span className='text-sm text-gray-900 dark:text-white'>{email}</span>
                </div>
                <button
                  onClick={() => handleRemoveNotificationEmail(email)}
                  disabled={isLoading}
                  className='text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 disabled:opacity-50'
                  title='削除'
                >
                  <XMarkIcon className='h-4 w-4' />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettingsForm;
